import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";

const Currency = () => {
  // ------------------------------
  // Dynamic Form Field Config
  // ------------------------------
  const formFields = useMemo(() => [
    { name: "code", label: "Currency Code", type: "text", required: true, col: 6 },
    { name: "currency_name", label: "Currency Name", type: "text", required: true, col: 6 },
    { name: "symbol", label: "Symbol", type: "text", col: 6 },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: 0, label: "Active" },
        { value: 1, label: "Inactive" },
      ],
      col: 6,
    },
  ], []);

  // ------------------------------
  // Initial Form State
  // ------------------------------
  const initialFormState = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, { currency_id: 0 });
  }, [formFields]);

  const [formData, setFormData] = useState(initialFormState);
  const [currencyList, setCurrencyList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    try {
      const response = await post("/masters/currency/list");
      if (response.status === 200) {
        setCurrencyList(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ------------------------------
  // Edit Handler (FIXED POSITION)
  // ------------------------------
  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });
    setShowModal(true);
  }, [initialFormState]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
      {
        header: "Actions",
        cell: ({ row }) => (
          <i
            className="ri-file-edit-fill text-primary cursor-pointer me-2"
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ fontSize: "18px" }}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      { header: "Code", accessorKey: "code" },
      { header: "Name", accessorKey: "currency_name" },
      { header: "Symbol", accessorKey: "symbol" },
      { header: "Status", accessorKey: "status", label_condition: "status" },
    ],
    [handleEdit]
  );

  // ------------------------------
  // Input Change Handler
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ------------------------------
  // Close Modal
  // ------------------------------
  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
  };

  // ------------------------------
  // Submit Form
  // ------------------------------
  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      const response = await post("/masters/currency", formData);
      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Currency</div>
          <div className="page-header-sub">Currency master used for job invoice values</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          Add Currency
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Currency List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Currency List"
            columns={columns}
            data={currencyList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        title="Currency"
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />
    </>
  );
};

export default Currency;
